import { useCallback, useEffect, useMemo, useState } from 'preact/hooks';
import TerrainBandBackToMapButton from './TerrainBandBackToMapButton.jsx';

function normalizeImageUrls(listingMedia) {
  if (!Array.isArray(listingMedia)) return [];
  return listingMedia
    .map((m) => {
      if (typeof m === 'string') return m.trim();
      if (m && typeof m.url === 'string') return m.url.trim();
      return '';
    })
    .filter(Boolean);
}

/**
 * Terrain band harita kabuğunda ilan galerisi overlay'i.
 * Eğim / yol / sokak görüntüsü ile aynı exclusivity modelini kullanır.
 */
export default function TerrainBandImagesOverlay({
  open = false,
  listingMedia = null,
  onClose,
}) {
  const urls = useMemo(() => normalizeImageUrls(listingMedia), [listingMedia]);
  const [index, setIndex] = useState(0);

  useEffect(() => {
    if (!open) return undefined;
    setIndex(0);
    const onKey = (event) => {
      if (event.key === 'Escape') onClose?.();
      if (event.key === 'ArrowLeft') {
        setIndex((i) => (urls.length ? (i - 1 + urls.length) % urls.length : 0));
      }
      if (event.key === 'ArrowRight') {
        setIndex((i) => (urls.length ? (i + 1) % urls.length : 0));
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, onClose, urls.length]);

  const goPrev = useCallback(() => {
    if (!urls.length) return;
    setIndex((i) => (i - 1 + urls.length) % urls.length);
  }, [urls.length]);

  const goNext = useCallback(() => {
    if (!urls.length) return;
    setIndex((i) => (i + 1) % urls.length);
  }, [urls.length]);

  if (!open) return null;

  const safeIndex = urls.length ? Math.min(Math.max(index, 0), urls.length - 1) : 0;
  const mainUrl = urls[safeIndex] || '';

  return (
    <div
      className="pp-detail-terrain-band__mesh-expand pp-detail-terrain-band__images-overlay"
      role="dialog"
      aria-modal="true"
      aria-label="İlan resimleri"
    >
      <div className="pp-detail-terrain-band__mesh-expand-bar">
        <span className="pp-detail-terrain-band__mesh-expand-title">ilan resimleri</span>
        <div className="pp-detail-terrain-band__mesh-expand-actions">
          <TerrainBandBackToMapButton onClick={onClose} />
        </div>
      </div>
      <div className="pp-detail-terrain-band__mesh-expand-body pp-detail-terrain-band__images-body">
        {!urls.length ? (
          <div className="pp-detail-empty" role="status">Bu ilana ait görsel yok.</div>
        ) : (
          <div className="pp-detail-images-gallery pp-detail-images-gallery--terrain">
            <div className="pp-detail-images-main">
              <div className="pp-detail-images-main-frame pp-detail-images-main-frame--hero">
                <div className="pp-detail-images-main-frame-inner">
                  <img
                    src={mainUrl}
                    alt="İlan görseli"
                    className="pp-detail-images-main-img"
                    loading="eager"
                    decoding="async"
                    draggable={false}
                  />
                  {urls.length > 1 ? (
                    <>
                      <button
                        type="button"
                        className="pp-detail-images-nav-hit pp-detail-images-nav-hit--prev"
                        aria-label="Önceki görsel"
                        onClick={goPrev}
                      />
                      <button
                        type="button"
                        className="pp-detail-images-nav-hit pp-detail-images-nav-hit--next"
                        aria-label="Sonraki görsel"
                        onClick={goNext}
                      />
                    </>
                  ) : null}
                </div>
              </div>
              <p className="pp-detail-images-main-caption">
                <span>
                  {safeIndex + 1} / {urls.length}
                </span>
              </p>
            </div>
            {urls.length > 1 ? (
              <div className="pp-detail-images-thumbs pp-detail-images-thumbs--hero" role="list">
                {urls.map((url, i) => (
                  <button
                    key={`${url}-${i}`}
                    type="button"
                    role="listitem"
                    className={`pp-detail-images-thumb${i === safeIndex ? ' is-selected' : ''}`}
                    onClick={() => setIndex(i)}
                    title={`Görsel ${i + 1}`}
                    aria-pressed={i === safeIndex}
                  >
                    <img src={url} alt="" className="pp-detail-images-thumb-img" loading="lazy" decoding="async" />
                  </button>
                ))}
              </div>
            ) : null}
          </div>
        )}
      </div>
    </div>
  );
}
